import type { PayloadAction } from '@reduxjs/toolkit';
import type { ApiCustomCurriculum } from '../../types/llinkidApiTypes';
import { generalLoadError, generalSaveError } from '@store/alerts/alertConstants';
import { setAlert } from '@store/alerts/alertsState';
import { loadCurriculum, loadGoals } from '@store/contentApi/contentApiState';
import { makeDistributionWindowObject } from '@store/distribute/distributeHelper';
import { selectDistributionData } from '@store/distribute/distributeSelectors';
import type { RootState } from '@store/storeSetup';
import { tryCreateCustomCurriculaAndWaitForResponse } from '@store/leerplanList/leerplanListSagas';
import { selectCustomCurriculaData } from '@store/llinkidApis/llinkidApiSelectors';
import {
  addCacheItem,
  applyBatch,
  deltaUpdateCache,
  saveLlinkidApiBatch,
} from '@store/llinkidApis/llinkidApiState';
import { selectCustomCurriculaFromFilters } from '@store/llinkidApis/userActionsSelectors';
import { generateRemovedDistributeSectionBatch } from '@utils/distributionHelper';
import { getKeyFromHref } from '@utils/getKeyFromHref';
import { logAndCaptureException } from '@utils/logAndCaptureException';
import { typedSelect, waitForSaga } from '@utils/sagaUtils';
import { getNonDerivedGroupKey } from '@utils/utils';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  selectAreCustomCurriculaMissing,
  selectCustomCurriculaFromUrlOrParams,
  selectCustomCurriculumFromKey,
  selectExtraGoalsToLoad,
  selectParentTreeMap,
  selectSelectedStudyPrograms,
} from './curriculumSelectors';
import type {
  LoadGoalsForCurriculumParamsPayload,
  RemoveCustomSectionPayload,
} from './curriculumTypes';
import { loadGoalsForCurriculumParams, removeCustomSection, setUrlParams } from './curriculumState';
import type { BatchRequest } from '../../types/sriTypes';

function* loadGoalsSaga({ payload }: PayloadAction<LoadGoalsForCurriculumParamsPayload>) {
  try {
    const { curriculumKey, preview } = payload;

    if (curriculumKey && curriculumKey !== 'nonderived') {
      yield put(loadCurriculum({ curriculumKey, refresh: preview }));
      yield call(waitForSaga, (state: RootState) => {
        return (
          curriculumKey === 'nonderived' ||
          state.contentApiData.curricula[curriculumKey] ||
          state.contentApiData.curriculaToLoad[curriculumKey]?.isFailed === true
        );
      });
    }

    const goalsToLoad: string[] = yield* typedSelect(selectExtraGoalsToLoad);

    yield put(loadGoals({ keys: goalsToLoad.map((e) => getKeyFromHref(e)), refresh: preview }));
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalLoadError));
  }
}

function* loadExtraData() {
  try {
    yield call(waitForSaga, (state: RootState) => selectCustomCurriculaData(state).length);
    yield call(
      waitForSaga,
      (state: RootState) => Object.keys(state.customCurriculaData.annotations).length,
    );

    const goalsToLoad: string[] = yield* typedSelect(selectExtraGoalsToLoad);
    if (goalsToLoad.length) {
      const refresh: boolean = yield* typedSelect((state: RootState) => state.curriculum.preview);
      yield put(loadGoals({ keys: goalsToLoad.map((e) => getKeyFromHref(e)), refresh }));
    }
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalLoadError));
  }
}

// this function checks if there are custom curricula missing in the studyProgrammeGroup (for example: A-stroom) and creates them if so
function* checkForMissingCustomCur() {
  try {
    yield call(waitForSaga, (state: RootState) => selectCustomCurriculaData(state).length);
    const tryCreateCurrBatchInfo = yield* typedSelect(selectAreCustomCurriculaMissing);
    if (tryCreateCurrBatchInfo) {
      yield call(
        tryCreateCustomCurriculaAndWaitForResponse,
        tryCreateCurrBatchInfo.curriculum,
        tryCreateCurrBatchInfo.creator,
        tryCreateCurrBatchInfo.studyPrograms,
      );
    }
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

function* removeCustomSectionSaga(action: PayloadAction<RemoveCustomSectionPayload>) {
  try {
    const { section } = action.payload;

    const parentVMap = yield* typedSelect(selectParentTreeMap);

    const parentNode = parentVMap.get(section.key).next;
    const parent = parentNode.value;

    const customCurricula: ApiCustomCurriculum[] = yield* typedSelect(selectCustomCurriculaData);

    const distributionData = yield* typedSelect(selectDistributionData);

    if (!distributionData) {
      throw new Error('DistributionData not loaded');
    }

    const customCurriculumIds: string[] = yield* typedSelect((state: RootState) =>
      selectCustomCurriculaFromUrlOrParams(state).map((c: any) => c.key),
    );
    const customCur = yield* typedSelect((state: RootState) =>
      selectCustomCurriculumFromKey(state, customCurriculumIds[0]),
    );
    const cur = distributionData.allSubjects.find(
      (c: any) => getNonDerivedGroupKey(customCurricula, customCur) === c.groupKey,
    );
    const resource: any = makeDistributionWindowObject(cur, distributionData.allReferences);

    const selectedStudyProgramHrefs: string[] = yield* typedSelect((state: RootState) =>
      selectSelectedStudyPrograms(state).map((e: any) => e.$$meta.permalink),
    );

    const selectedProgramsForCur = resource.studyProgrammes
      .map((e: any) => e.href)
      .filter((e: string) => selectedStudyProgramHrefs.includes(e));

    const customCurs: ApiCustomCurriculum[] = yield* typedSelect((state: RootState) =>
      selectCustomCurriculaFromFilters(state, {
        curriculum: resource,
        studyPrograms: selectedProgramsForCur,
      }),
    );

    let allBatches: BatchRequest = [];
    allBatches.push({ verb: 'DELETE', href: section.$$meta.permalink });

    const batch: BatchRequest = generateRemovedDistributeSectionBatch({
      section,
      selectedProgramsForCur,
      customCurs,
      allReferencesList: distributionData.allReferences,
      parent,
    });

    allBatches = allBatches.concat(batch);

    yield put(saveLlinkidApiBatch({ batch: allBatches, applyInstantly: false }));
  } catch (e) {
    logAndCaptureException(e);
    yield put(setAlert(generalSaveError));
  }
}

export function* watchCurriculumSaga() {
  yield takeLatest([setUrlParams, applyBatch, addCacheItem, deltaUpdateCache], loadExtraData);
  yield takeEvery(setUrlParams, checkForMissingCustomCur);
  yield takeEvery(loadGoalsForCurriculumParams, loadGoalsSaga);
  yield takeEvery(removeCustomSection, removeCustomSectionSaga);
}
